import { RoomFilterType } from './RoomFilterType';
import RoomFilterWell from './RoomFilterWell';
import type RoomFilterWellItemViewState from './RoomFilterWellItemViewState';
import RoomPanelHeader from './RoomPanelHeader';
import RoomResultsList from './RoomResultsList';
import type { Room, RoomList, RoomSelectorFilters, RoomSelectorProps } from './RoomSelector.types';
import { setUserRoomListConfig } from '../../actions/calendarRoomActions';
import type { RoomPanelMetricInfo } from '../../store/schema/RoomPanelMetricTypes';
import getStore, { default as roomPanelViewState } from '../../store/store';
import { createRoomPanelMetrics } from '../../utils/createRoomPanelMetrics';
import logRoomPanelMetric, { getAvgRoomSelectedPerfData } from '../../utils/logRoomPanelMetrics';
import RoomFilterV2 from '../RoomFilter/RoomFilterV2';
import { KeyCodes } from '@fluentui/utilities';
import { ThemeProvider } from '@fluentui/react/lib/utilities/ThemeProvider';
import { observer } from 'mobx-react-lite';
import { ComboBox, IComboBox, IComboBoxOption } from '@fluentui/react/lib/ComboBox';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { PerformanceDatapoint } from 'owa-analytics';
import { themeFromColorScheme } from 'owa-color-utils/lib/colorUtils';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { isFeatureEnabled } from 'owa-feature-flags';
import { freeBusyCache } from 'owa-freebusy-cache';
import loc from 'owa-localize';
import { anyRoomListName } from 'owa-locstrings/lib/strings/anyroomlistname.locstring.json';
import * as React from 'react';
import {
    showOnlyAvailableRoomsToggleLabel,
    roomListTitle,
} from './RoomSelector.base.locstring.json';
import getPlaceholderRoomLists from '../../selectors/getPlaceholderRoomLists';
/* tslint:disable:jsx-no-lambda WI:47594 */

let styles = require('./RoomSelector.scss');
let classNames = require('classnames/bind').bind(styles);

const assign = require('object-assign');
const ROOMSELECTEDCHECKMARK = 'RoomSelected';

/** The base non-styled version of RoomSelector. */
export const RoomSelectorBase = observer(function RoomSelectorBase(props: RoomSelectorProps) {
    const perfDatapoint = React.useRef<PerformanceDatapoint>();
    const dropDownRef = React.useRef<IComboBox>();
    const [roomDropdownItems] = React.useState<IComboBoxOption[]>(() => {
        return (getStore().roomListViewState.roomLists || []).map(roomlist => ({
            key: roomlist.email,
            text: roomlist.name,
            title: roomlist.name,
            styles: {
                optionText: styles.roomSelectorOption,
                optionTextWrapper: styles.roomSelectorOptionWrapper,
            },
        }));
    });
    const usageDatapoint = React.useRef<RoomPanelMetricInfo>();

    const [roomFilters, setRoomFilters] = React.useState<RoomSelectorFilters>(() => {
        let initialRoomLists: RoomList[] = [];

        if (!isFeatureEnabled('cal-advancedRoomBooking')) {
            let tempRoomList = getStore().roomListViewState.selectedRoomList;

            if (!tempRoomList || !tempRoomList.email) {
                tempRoomList = {
                    name: loc(anyRoomListName),
                    email: loc(anyRoomListName),
                };
            }

            initialRoomLists = [tempRoomList];
        }

        return {
            roomLists: initialRoomLists,
            showOnlyAvailableRooms: !isFeatureEnabled('cal-mf-advancedRoomBookingV2'),
        };
    });

    React.useEffect(() => {
        // The perfDataPoint will time out (stop tracking elapsed time inside the panel)
        // after 30 minutes.  Note that if not set, the default time out is only 1 minute.
        const maxPerfDataPointTimeOut: number = 1800000;
        perfDatapoint.current = new PerformanceDatapoint('RoomPanelPerf', {
            timeout: maxPerfDataPointTimeOut,
        });
        perfDatapoint.current.addCustomData({
            RoomPanelEntrySource: props.source,
        });
        usageDatapoint.current = createRoomPanelMetrics();

        return () => {
            // This is needed in case the compose form or scheduling assistant is closed
            // while the room booking panel is still open.
            // This becomes a NOOP if the room booking panel is closed via the panel's
            // close button.
            getAvgRoomSelectedPerfData(perfDatapoint.current, ROOMSELECTEDCHECKMARK);
            perfDatapoint.current.end();
            assign(usageDatapoint.current, roomPanelViewState().roomPanelMetricsState);
            logRoomPanelMetric(usageDatapoint.current);
        };
    }, []);

    const onRoomSelected = (room: Room) => {
        props.onRoomAdded(room);
        perfDatapoint.current.addCheckmark(ROOMSELECTEDCHECKMARK);
    };

    const onRoomFilterWellItemsChanged = (items: RoomFilterWellItemViewState[]): void => {
        let roomLists: RoomList[] = [];
        let cities: string[] = [];
        let tempRoomFilters: RoomSelectorFilters = {
            ...roomFilters,
            roomLists: [],
            cities: [],
        };

        if (items && items.length > 0) {
            roomLists = items
                .filter(x => x.filterType == RoomFilterType.RoomList)
                .map(x => {
                    return {
                        key: x.queryableId,
                        email: x.queryableId,
                        name: x.displayText,
                    };
                });
            tempRoomFilters.roomLists = roomLists;
            usageDatapoint.current.numOfRoomlists = roomLists.length;

            cities = items.filter(x => x.filterType == RoomFilterType.City).map(x => x.displayText);
            tempRoomFilters.cities = cities;
            usageDatapoint.current.numOfCities = cities.length;
        } else {
            roomLists = getPlaceholderRoomLists();
        }

        setRoomFilters(tempRoomFilters);
        onRoomFiltersChanged({ cities: cities, roomLists: roomLists });
    };

    const onRoomListChanged = (event: React.FormEvent<IComboBox>, item: IComboBoxOption) => {
        if (!item) {
            return;
        }

        let roomList: RoomList = {
            email: item.key.toString(),
            name: item.text.toString(),
        };

        const roomFilters = { roomLists: [roomList] };
        setRoomFilters(roomFilters);
        onRoomFiltersChanged(roomFilters);
    };

    const onShowOnlyAvailableRoomsChanged = (changed: boolean) => {
        usageDatapoint.current.hasShowOnlyAvailableRoomsChanged = true;
        setRoomFilters({ ...roomFilters, showOnlyAvailableRooms: changed });
    };

    const onRoomFiltersChanged = (newFilters: RoomSelectorFilters) => {
        setUserRoomListConfig({
            roomLists: newFilters.roomLists ?? roomFilters.roomLists,
            cities: newFilters.cities ?? roomFilters.cities,
        });
    };

    const onRoomPanelClose = (): void => {
        const elapsedTimePropertyName: string = 'E2ETimeElapsed';

        perfDatapoint.current.end();
        props.onClose(+perfDatapoint.current.properties[elapsedTimePropertyName]);
    };

    const onKeyDown = (evt: React.KeyboardEvent<any>) => {
        if (evt.which == KeyCodes.escape) {
            evt.stopPropagation();
            evt.preventDefault();
            onRoomPanelClose();
        }
    };

    return (
        <ThemeProvider
            theme={themeFromColorScheme(props.colorScheme)}
            className={styles.roomSelectorThemeProvider}>
            <div className={styles.container} data-is-scrollable={true} onKeyDown={onKeyDown}>
                {!props.hideHeader && <RoomPanelHeader onRoomPanelClose={onRoomPanelClose} />}
                {isFeatureEnabled('cal-advancedRoomBooking') ? (
                    <div
                        className={classNames(styles.roomFiltersSection, {
                            v2: isFeatureEnabled('cal-mf-advancedRoomBookingV2'),
                        })}>
                        {isFeatureEnabled('cal-mf-advancedRoomBookingV2') ? (
                            <RoomFilterV2
                                colorScheme={props.colorScheme}
                                onRoomFilterWellItemsChanged={onRoomFilterWellItemsChanged}
                                onPlaceTypeChanged={props.onPlaceTypeChanged}
                            />
                        ) : (
                            <>
                                <RoomFilterWell
                                    colorScheme={props.colorScheme}
                                    containerClassName={styles.roomFilterWellContainer}
                                    defaultSelectedItems={[]}
                                    draggableItemType={DraggableItemTypes.ReadWriteRecipient}
                                    dropViewState={null}
                                    onRoomFilterWellItemsChanged={onRoomFilterWellItemsChanged}
                                    wellClassName={styles.wellClassName}
                                />

                                <Toggle
                                    className={styles.toggleContainer}
                                    onText={loc(showOnlyAvailableRoomsToggleLabel)}
                                    offText={loc(showOnlyAvailableRoomsToggleLabel)}
                                    checked={roomFilters.showOnlyAvailableRooms}
                                    onChanged={onShowOnlyAvailableRoomsChanged}
                                    styles={{
                                        root: { marginBottom: 5, marginTop: 17 },
                                    }}
                                />
                            </>
                        )}
                    </div>
                ) : (
                    <div className={styles.roomFiltersSection}>
                        <ComboBox
                            defaultSelectedKey={roomFilters.roomLists[0].email}
                            label={loc(roomListTitle)}
                            id="RoomListSelector"
                            ariaLabel={loc(roomListTitle)}
                            options={roomDropdownItems}
                            onChange={onRoomListChanged}
                            selectedKey={roomFilters.roomLists[0].email}
                            componentRef={dropDownRef}
                            allowFreeform={true}
                            autoComplete="on"
                            styles={{
                                root: styles.roomSelectorRoot,
                                callout: styles.roomSelectorCalloutOld,
                                header: styles.roomSelectorHeaders,
                                label: styles.roomSelectorLabel,
                                optionsContainer: styles.roomSelectorLabel,
                            }}
                            caretDownButtonStyles={{
                                root: styles.roomSelectorCaretDown,
                            }}
                            onPendingValueChanged={() => dropDownRef.current.focus(true)}
                        />
                    </div>
                )}
                <RoomResultsList
                    calendarEvent={props.event}
                    colorScheme={props.colorScheme}
                    freeBusyCacheManager={freeBusyCache}
                    onRoomSelected={onRoomSelected}
                    roomFilters={roomFilters}
                />
            </div>
        </ThemeProvider>
    );
});
